import { useState } from 'react'
import {
  type CustomWorkflowRun,
  type CustomWorkflowRunForm,
  type CustomWorkflowRunInputValue,
  useGetCustomWorkflowRunFormQuery,
  useSubmitCustomWorkflowRunFormMutation,
} from '../../store/customWorkflow/customWorkflows'

interface UsePendingRunFormArgs {
  accountId: string
  workflowId: string
  runId: string | null
  run: CustomWorkflowRun | null | undefined
}

export interface PendingRunForm {
  form: CustomWorkflowRunForm | undefined
  locked: boolean
  awaitingInput: boolean
  loadError: boolean
  submitted: boolean
  submitting: boolean
  submitError: string | null
  submit: (action: string, inputs: Record<string, CustomWorkflowRunInputValue>) => Promise<void>
}

const SUBMIT_ERROR = 'Failed to submit the input — it may have been answered already.'

export const usePendingRunForm = ({ accountId, workflowId, runId, run }: UsePendingRunFormArgs): PendingRunForm => {
  const [submittedForm, setSubmittedForm] = useState<{ runId: string; form: CustomWorkflowRunForm } | null>(null)
  const [submitError, setSubmitError] = useState<{ formId: string; message: string } | null>(null)

  const paused = run?.status === 'paused'
  const pendingFormId = paused ? run.pendingFormId : null
  const lastSubmitted = submittedForm !== null && submittedForm.runId === runId ? submittedForm.form : null
  const awaitingFormId = pendingFormId !== null && pendingFormId !== lastSubmitted?.id ? pendingFormId : null

  const { currentData: awaitingForm, isError: loadError } = useGetCustomWorkflowRunFormQuery(
    { accountId, workflowId, runId: runId ?? '', formId: awaitingFormId ?? '' },
    { skip: !runId || !awaitingFormId },
  )

  const [submitForm, { isLoading: submitting }] = useSubmitCustomWorkflowRunFormMutation()

  const lockedForm = paused && awaitingFormId === null ? lastSubmitted : null
  const form = awaitingFormId !== null ? awaitingForm : (lockedForm ?? undefined)

  const submit = async (action: string, inputs: Record<string, CustomWorkflowRunInputValue>) => {
    if (!runId || !awaitingForm) {
      return
    }
    setSubmitError(null)
    try {
      await submitForm({ accountId, workflowId, runId, formId: awaitingForm.id, inputs, action }).unwrap()
      setSubmittedForm({ runId, form: awaitingForm })
    } catch {
      setSubmitError({ formId: awaitingForm.id, message: SUBMIT_ERROR })
    }
  }

  return {
    form,
    locked: form !== undefined && form === lockedForm,
    awaitingInput: awaitingFormId !== null,
    loadError: awaitingFormId !== null && loadError,
    submitted: lastSubmitted !== null,
    submitting,
    submitError: submitError !== null && submitError.formId === form?.id ? submitError.message : null,
    submit,
  }
}
